import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { adminAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, Eye, ArrowLeft, Save,
  FileText, Globe, Tag, Type, AlignLeft, Heading1, Heading2, Heading3,
  Bold, List, ListOrdered, Minus, ExternalLink, Columns, PenLine, EyeIcon, Link2, Table2
} from 'lucide-react';

// ==================== INLINE MARKUP PARSER ====================
function parseInlineMarkup(text, keyPrefix = '') {
  const parts = [];
  const regex = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)|\*\*([^*]+)\*\*/g;
  let lastIndex = 0;
  let match;
  let idx = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[1] && match[2]) {
      parts.push(
        <a key={`${keyPrefix}l${idx}`} href={match[2]} target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--brand-gold-500))] underline underline-offset-2 hover:text-white transition-colors">{match[1]}</a>
      );
    } else if (match[3]) {
      parts.push(<strong key={`${keyPrefix}b${idx}`} className="text-white">{match[3]}</strong>);
    }
    lastIndex = match.index + match[0].length;
    idx++;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
}

// ==================== CONTENT PREVIEW RENDERER ====================
function ContentPreview({ content }) {
  if (!content) return null;
  const lines = content.split('\n');
  const elements = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    // Table block: collect consecutive | lines
    if (line.trimStart().startsWith('|')) {
      const tableLines = [];
      while (i < lines.length && lines[i].trimStart().startsWith('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      const parseCells = (row) => row.split('|').slice(1, -1).map(c => c.trim());
      const headerCells = parseCells(tableLines[0]);
      const dataRows = tableLines.filter((r, idx) => idx > 1).map(parseCells);
      elements.push(
        <div key={`tbl-${i}`} className="my-4 overflow-x-auto rounded-lg border border-[hsl(var(--border))]">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[hsl(var(--accent))]">
                {headerCells.map((c, ci) => <th key={ci} className="px-3 py-2 text-left text-white font-semibold border-b border-[hsl(var(--border))]">{parseInlineMarkup(c, `th${i}${ci}`)}</th>)}
              </tr>
            </thead>
            <tbody>
              {dataRows.map((row, ri) => (
                <tr key={ri} className={ri % 2 === 0 ? '' : 'bg-[hsl(var(--accent))]/30'}>
                  {row.map((c, ci) => <td key={ci} className="px-3 py-2 text-gray-300 border-b border-[hsl(var(--border))]/50">{parseInlineMarkup(c, `td${i}${ri}${ci}`)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }
    if (line.startsWith('### ')) {
      elements.push(<h3 key={`h3-${i}`} className="text-lg font-bold text-white mt-6 mb-2">{parseInlineMarkup(line.replace('### ', ''), `h3${i}`)}</h3>);
    } else if (line.startsWith('## ')) {
      elements.push(<h2 key={`h2-${i}`} className="text-xl font-bold text-white mt-8 mb-3">{parseInlineMarkup(line.replace('## ', ''), `h2${i}`)}</h2>);
    } else if (line.startsWith('# ')) {
      elements.push(<h1 key={`h1-${i}`} className="text-2xl font-bold text-[hsl(var(--brand-gold-500))] mt-8 mb-4">{parseInlineMarkup(line.replace('# ', ''), `h1${i}`)}</h1>);
    } else if (line.match(/^\d+\.\s/)) {
      elements.push(<p key={`ol-${i}`} className="text-gray-300 leading-relaxed mb-1.5 pl-4 text-sm">{parseInlineMarkup(line, `ol${i}`)}</p>);
    } else if (line.startsWith('- ')) {
      elements.push(<p key={`li-${i}`} className="text-gray-300 leading-relaxed mb-1 pl-4 text-sm">{parseInlineMarkup(line.substring(2), `li${i}`)}</p>);
    } else if (line.trim() === '') {
      elements.push(<div key={`br-${i}`} className="h-1.5" />);
    } else {
      elements.push(<p key={`t-${i}`} className="text-gray-300 leading-relaxed mb-1.5 text-sm">{parseInlineMarkup(line, `t${i}`)}</p>);
    }
    i++;
  }
  return elements;
}

// ==================== BLOG POST EDITOR ====================
function BlogEditor({ post, onSave, onCancel }) {
  const [title, setTitle] = useState(post?.title || '');
  const [content, setContent] = useState(post?.content || '');
  const [excerpt, setExcerpt] = useState(post?.excerpt || '');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState(post?.tags || []);
  const [status, setStatus] = useState(post?.status || 'draft');
  const [metaTitle, setMetaTitle] = useState(post?.meta_title || '');
  const [metaDescription, setMetaDescription] = useState(post?.meta_description || '');
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('content');
  const [viewMode, setViewMode] = useState('split');
  const textareaRef = React.useRef(null);

  const slug = post?.slug || (title ? title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') : '');

  const insertAtCursor = useCallback((prefix, suffix = '') => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = content.substring(start, end);
    const before = content.substring(0, start);
    const after = content.substring(end);
    const inserted = `${prefix}${selected}${suffix}`;
    setContent(before + inserted + after);
    setTimeout(() => {
      ta.focus();
      ta.selectionStart = start + prefix.length;
      ta.selectionEnd = start + prefix.length + selected.length;
    }, 0);
  }, [content]);

  const handleAddTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (t && !tags.includes(t)) {
      setTags([...tags, t]);
    }
    setTagInput('');
  };

  const handleRemoveTag = (tag) => {
    setTags(tags.filter(t => t !== tag));
  };

  const handleSave = async (saveStatus) => {
    if (!title.trim() || !content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setSaving(true);
    try {
      const data = {
        title: title.trim(),
        content: content.trim(),
        excerpt: excerpt.trim(),
        tags,
        status: saveStatus,
        meta_title: metaTitle.trim() || title.trim(),
        meta_description: metaDescription.trim() || excerpt.trim() || content.trim().substring(0, 160),
      };
      await onSave(data);
      toast.success(saveStatus === 'published' ? 'Post published!' : 'Draft saved!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const toolbarButtons = [
    { icon: Heading1, label: 'H1', action: () => insertAtCursor('\n# ', '\n') },
    { icon: Heading2, label: 'H2', action: () => insertAtCursor('\n## ', '\n') },
    { icon: Heading3, label: 'H3', action: () => insertAtCursor('\n### ', '\n') },
    { sep: true },
    { icon: Bold, label: 'Bold', action: () => insertAtCursor('**', '**') },
    { icon: Link2, label: 'Link', action: () => insertAtCursor('[', '](https://)') },
    { sep: true },
    { icon: List, label: 'Bullet', action: () => insertAtCursor('\n- ') },
    { icon: ListOrdered, label: 'Number', action: () => insertAtCursor('\n1. ') },
    { icon: Table2, label: 'Table', action: () => insertAtCursor('\n| Column 1 | Column 2 | Column 3 |\n| --- | --- | --- |\n| Cell 1 | Cell 2 | Cell 3 |\n| Cell 4 | Cell 5 | Cell 6 |\n') },
    { sep: true },
    { icon: Minus, label: 'Line Break', action: () => insertAtCursor('\n\n') },
  ];

  const sectionTabs = [
    { id: 'content', label: 'Content', icon: FileText },
    { id: 'seo', label: 'SEO & Meta', icon: Globe },
    { id: 'categories', label: 'Categories', icon: Tag },
  ];

  return (
    <div className="space-y-4" data-testid="blog-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onCancel} className="text-gray-300" data-testid="blog-editor-back">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Posts
        </Button>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleSave('draft')}
            disabled={saving}
            className="border-[hsl(var(--border))] text-gray-300"
            data-testid="blog-editor-save-draft"
          >
            <Save className="w-4 h-4 mr-2" /> Save Draft
          </Button>
          <Button
            onClick={() => handleSave('published')}
            disabled={saving}
            className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-gold-400))]"
            data-testid="blog-editor-publish"
          >
            <Globe className="w-4 h-4 mr-2" /> {post?.status === 'published' ? 'Update' : 'Publish'}
          </Button>
        </div>
      </div>

      {/* Title Input */}
      <div>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Post title..."
          className="text-2xl font-bold bg-transparent border-none text-white placeholder:text-gray-600 px-0 h-auto py-2 focus-visible:ring-0"
          data-testid="blog-editor-title"
        />
        {slug && (
          <p className="text-xs text-gray-500 mt-1">
            Slug: <span className="text-gray-400">/blog/{slug}</span>
          </p>
        )}
      </div>

      {/* Section Tabs */}
      <div className="flex gap-1 bg-[hsl(var(--secondary))] rounded-lg p-1">
        {sectionTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSection(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              activeSection === tab.id
                ? 'bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))]'
                : 'text-gray-400 hover:text-white hover:bg-[hsl(var(--accent))]'
            }`}
            data-testid={`blog-editor-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content Section */}
      {activeSection === 'content' && (
        <div className="space-y-3">
          {/* Excerpt */}
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Excerpt / Summary</label>
            <textarea
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              placeholder="Brief summary of the post (shown in post list cards)..."
              rows={2}
              className="w-full bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] rounded-lg p-3 text-white placeholder:text-gray-600 resize-none text-sm focus:outline-none focus:border-[hsl(var(--brand-gold-500))]/50"
              data-testid="blog-editor-excerpt"
            />
          </div>

          {/* Toolbar + View Toggle */}
          <div className="flex items-center gap-1 p-2 bg-[hsl(var(--secondary))] rounded-lg border border-[hsl(var(--border))]">
            {toolbarButtons.map((btn, i) =>
              btn.sep ? (
                <div key={`sep-${i}`} className="w-px h-6 bg-[hsl(var(--border))] mx-1" />
              ) : (
                <button
                  key={btn.label}
                  onClick={btn.action}
                  title={btn.label}
                  className="p-1.5 rounded hover:bg-[hsl(var(--accent))] text-gray-400 hover:text-white transition-colors"
                  data-testid={`blog-toolbar-${btn.label.toLowerCase()}`}
                >
                  <btn.icon className="w-4 h-4" />
                </button>
              )
            )}
            <div className="flex-1" />
            <div className="flex items-center gap-0.5 bg-[hsl(var(--accent))] rounded-md p-0.5">
              {[
                { id: 'write', icon: PenLine, label: 'Write' },
                { id: 'split', icon: Columns, label: 'Split' },
                { id: 'preview', icon: EyeIcon, label: 'Preview' },
              ].map(mode => (
                <button
                  key={mode.id}
                  onClick={() => setViewMode(mode.id)}
                  title={mode.label}
                  className={`p-1.5 rounded text-xs flex items-center gap-1 transition-colors ${
                    viewMode === mode.id
                      ? 'bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))]'
                      : 'text-gray-400 hover:text-white'
                  }`}
                  data-testid={`blog-view-mode-${mode.id}`}
                >
                  <mode.icon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{mode.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Editor + Preview */}
          <div className={`${viewMode === 'split' ? 'grid grid-cols-2 gap-3' : ''}`}>
            {/* Editor Pane */}
            {viewMode !== 'preview' && (
              <textarea
                ref={textareaRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={`Write your blog post content here...\n\nFormatting guide:\n# Heading 1\n## Heading 2\n### Heading 3\n**Bold text**\n- Bullet point\n1. Numbered list`}
                rows={viewMode === 'split' ? 24 : 20}
                className="w-full bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] rounded-lg p-4 text-gray-200 placeholder:text-gray-600 resize-y font-mono text-sm leading-relaxed focus:outline-none focus:border-[hsl(var(--brand-gold-500))]/50"
                data-testid="blog-editor-content"
              />
            )}

            {/* Preview Pane */}
            {viewMode !== 'write' && (
              <div
                className="bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] rounded-lg p-6 overflow-y-auto"
                style={{ minHeight: viewMode === 'split' ? '560px' : '480px', maxHeight: '700px' }}
                data-testid="blog-editor-preview"
              >
                {!content.trim() ? (
                  <div className="h-full flex items-center justify-center text-gray-600 text-sm">
                    <div className="text-center">
                      <EyeIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      <p>Live preview will appear here</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    {title && (
                      <h1 className="text-2xl sm:text-3xl font-bold text-[hsl(var(--brand-gold-500))] mb-4 leading-tight">{title}</h1>
                    )}
                    {excerpt && (
                      <p className="text-gray-400 text-sm mb-4 italic">{excerpt}</p>
                    )}
                    {tags.length > 0 && (
                      <div className="flex gap-1.5 mb-5 flex-wrap">
                        {tags.map(t => (
                          <span key={t} className="text-xs px-2 py-0.5 rounded border border-[hsl(var(--border))] text-gray-500">{t}</span>
                        ))}
                      </div>
                    )}
                    <div className="border-t border-[hsl(var(--border))] pt-5">
                      <ContentPreview content={content} />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* SEO Section */}
      {activeSection === 'seo' && (
        <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
          <CardContent className="p-6 space-y-5">
            <div>
              <label className="text-sm text-gray-400 mb-1.5 block">Meta Title</label>
              <Input
                value={metaTitle}
                onChange={(e) => setMetaTitle(e.target.value)}
                placeholder={title || 'Page title for search engines...'}
                className="bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white"
                data-testid="blog-editor-meta-title"
              />
              <p className="text-xs text-gray-600 mt-1">
                {(metaTitle || title).length}/60 characters
                {(metaTitle || title).length > 60 && <span className="text-amber-500 ml-1">(Recommended: under 60)</span>}
              </p>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1.5 block">Meta Description</label>
              <textarea
                value={metaDescription}
                onChange={(e) => setMetaDescription(e.target.value)}
                placeholder={excerpt || 'Description for search engine results...'}
                rows={3}
                className="w-full bg-[hsl(var(--accent))] border border-[hsl(var(--border))] rounded-lg p-3 text-white placeholder:text-gray-600 resize-none text-sm focus:outline-none focus:border-[hsl(var(--brand-gold-500))]/50"
                data-testid="blog-editor-meta-description"
              />
              <p className="text-xs text-gray-600 mt-1">
                {(metaDescription || excerpt).length}/160 characters
                {(metaDescription || excerpt).length > 160 && <span className="text-amber-500 ml-1">(Recommended: under 160)</span>}
              </p>
            </div>

            {/* SEO Preview */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Search Engine Preview</label>
              <div className="bg-white rounded-lg p-4">
                <p className="text-blue-600 text-lg font-medium leading-snug truncate">
                  {metaTitle || title || 'Post Title'} | JepPrep Blog
                </p>
                <p className="text-green-700 text-sm mt-0.5">
                  jepprep.com/blog/{slug || 'post-slug'}
                </p>
                <p className="text-gray-600 text-sm mt-1 line-clamp-2">
                  {metaDescription || excerpt || 'Post description will appear here...'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Categories Section */}
      {activeSection === 'categories' && (
        <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
          <CardContent className="p-6 space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-1.5 block">Tags / Categories</label>
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }}
                  placeholder="Type a tag and press Enter..."
                  className="bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white flex-1"
                  data-testid="blog-editor-tag-input"
                />
                <Button
                  onClick={handleAddTag}
                  variant="outline"
                  className="border-[hsl(var(--border))] text-gray-300"
                  data-testid="blog-editor-add-tag"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {tags.length === 0 ? (
                <p className="text-gray-600 text-sm">No tags added yet</p>
              ) : (
                tags.map(tag => (
                  <Badge
                    key={tag}
                    className="bg-[hsl(var(--accent))] text-gray-300 border-[hsl(var(--border))] px-3 py-1 cursor-pointer hover:border-red-500/50 group"
                    onClick={() => handleRemoveTag(tag)}
                    data-testid={`blog-tag-${tag}`}
                  >
                    {tag}
                    <span className="ml-2 text-gray-500 group-hover:text-red-400">x</span>
                  </Badge>
                ))
              )}
            </div>
            <p className="text-xs text-gray-600">Click a tag to remove it. Tags help with blog organization and SEO.</p>
          </CardContent>
        </Card>
      )}

      {/* Status */}
      <div className="flex items-center gap-4 pt-2">
        <span className="text-sm text-gray-400">Status:</span>
        <Badge className={post?.status === 'published' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-amber-500/20 text-amber-400 border-amber-500/30'}>
          {post?.status === 'published' ? 'Published' : 'Draft'}
        </Badge>
        {post?.slug && (
          <a
            href={`/blog/${post.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-[hsl(var(--brand-gold-500))] hover:text-white flex items-center gap-1 transition-colors"
          >
            <ExternalLink className="w-3 h-3" /> View Live
          </a>
        )}
      </div>
    </div>
  );
}

// ==================== BLOG TAB (LIST) ====================
export default function BlogTab() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const loadPosts = useCallback(async () => {
    try {
      const res = await adminAPI.listBlogPosts();
      setPosts(res.data.posts || []);
    } catch {
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const handleCreate = async (data) => {
    await adminAPI.createBlogPost(data);
    setIsCreating(false);
    loadPosts();
  };

  const handleUpdate = async (data) => {
    await adminAPI.updateBlogPost(editingPost.id, data);
    setEditingPost(null);
    loadPosts();
  };

  const handleDelete = async (postId) => {
    try {
      await adminAPI.deleteBlogPost(postId);
      toast.success('Post deleted');
      loadPosts();
    } catch {
      toast.error('Failed to delete post');
    } finally {
      setDeletingId(null);
    }
  };

  const handleEdit = async (postId) => {
    try {
      const res = await adminAPI.getBlogPost(postId);
      setEditingPost(res.data.post);
    } catch {
      toast.error('Failed to load post');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Editor View
  if (isCreating) {
    return <BlogEditor onSave={handleCreate} onCancel={() => setIsCreating(false)} />;
  }
  if (editingPost) {
    return <BlogEditor post={editingPost} onSave={handleUpdate} onCancel={() => setEditingPost(null)} />;
  }

  // List View
  return (
    <div className="space-y-6" data-testid="blog-admin-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Blog Posts</h2>
        <Button
          onClick={() => setIsCreating(true)}
          className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-gold-400))]"
          data-testid="blog-create-new-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> New Post
        </Button>
      </div>

      {loading ? (
        <div className="text-gray-400 text-center py-12">Loading blog posts...</div>
      ) : posts.length === 0 ? (
        <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
          <CardContent className="p-12 text-center">
            <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg mb-4">No blog posts yet</p>
            <Button
              onClick={() => setIsCreating(true)}
              className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))]"
            >
              <Plus className="w-4 h-4 mr-2" /> Create Your First Post
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {posts.map(post => (
            <Card key={post.id} className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] hover:border-[hsl(var(--brand-gold-500))]/30 transition-all" data-testid={`blog-admin-post-${post.id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-white font-medium truncate">{post.title}</h3>
                    <Badge className={
                      post.status === 'published'
                        ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs'
                        : 'bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs'
                    }>
                      {post.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span>{post.author}</span>
                    <span>Updated {formatDate(post.updated_at)}</span>
                    {post.tags?.length > 0 && (
                      <span className="flex gap-1">
                        {post.tags.slice(0, 3).map(t => (
                          <span key={t} className="text-gray-600">#{t}</span>
                        ))}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {post.status === 'published' && (
                    <a
                      href={`/blog/${post.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded hover:bg-[hsl(var(--accent))] text-gray-400 hover:text-white transition-colors"
                      title="View live"
                      data-testid={`blog-view-${post.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </a>
                  )}
                  <button
                    onClick={() => handleEdit(post.id)}
                    className="p-2 rounded hover:bg-[hsl(var(--accent))] text-gray-400 hover:text-white transition-colors"
                    title="Edit"
                    data-testid={`blog-edit-${post.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  {deletingId === post.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDelete(post.id)}
                        className="px-2 py-1 rounded bg-red-500/20 text-red-400 text-xs hover:bg-red-500/30"
                        data-testid={`blog-confirm-delete-${post.id}`}
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => setDeletingId(null)}
                        className="px-2 py-1 rounded text-gray-400 text-xs hover:text-white"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeletingId(post.id)}
                      className="p-2 rounded hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-colors"
                      title="Delete"
                      data-testid={`blog-delete-${post.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
